import React, { memo, useCallback, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { ChatInputBar } from '@/components/residents/marketplace/shared';
import type { ChatInputTheme } from '@/components/residents/marketplace/shared';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { CORK_BOARD_INPUT_LIMITS } from '@/constants/residents/corkBoardConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

interface ReplyInputProps {
  isLocked: boolean;
  onSubmit: (body: string) => Promise<boolean>;
  isSubmitting: boolean;
}

export const ReplyInput: React.FC<ReplyInputProps> = memo(function ReplyInput({ isLocked, onSubmit, isSubmitting }) {
  const { colors, semantic } = useThemeColors();
  const [text, setText] = useState('');

  const inputTheme: ChatInputTheme = {
    backgroundColor: semantic.background.primary,
    textColor: colors.text.primary,
    placeholderColor: colors.text.dimmer,
    iconColor: colors.text.dimmer,
    accentColor: PRIMITIVE_COLORS.resioBlue,
    previewBackgroundColor: colors.card.bg,
  };

  const handleSend = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || isSubmitting) return;
    const success = await onSubmit(trimmed);
    if (success) setText('');
  }, [text, isSubmitting, onSubmit]);

  if (isLocked) {
    return (
      <View
        style={[styles.lockedBanner, { borderTopColor: colors.separator, backgroundColor: semantic.background.primary }]}
        accessibilityRole="alert"
        accessibilityLabel="This thread is locked. Replies are disabled."
      >
        <RESIDENTS_SVG_COMPONENTS.PADLOCK width={14} height={14} color={colors.text.dimmer} />
        <Text style={[styles.lockedText, { color: colors.text.dimmer }]}>This thread is locked. Replies are disabled.</Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: semantic.background.primary }]}>
      <ChatInputBar
        value={text}
        onChangeText={setText}
        onSend={handleSend}
        placeholder="Write a reply..."
        maxLength={CORK_BOARD_INPUT_LIMITS.bodyMax}
        isSending={isSubmitting}
        theme={inputTheme}
        sendButtonText="Reply"
        paddingBottom={8}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  lockedBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderTopWidth: 1,
  },
  lockedText: {
    fontSize: 13,
    fontStyle: 'italic',
  },
});
